import { useState } from 'react';
import { testFormula } from '../services/parameterService';
import { Modal } from '../components/ui/Modal';
import { TextArea } from '../components/ui/TextArea';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';

interface ParameterTestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  formula: string;
}

interface SampleVariable {
  name: string;
  value: string;
}

export default function ParameterTestDialog({ isOpen, onClose, formula }: ParameterTestDialogProps) {
  const [formulaText, setFormulaText] = useState(formula);
  const [variables, setVariables] = useState<SampleVariable[]>([{ name: '', value: '' }]);
  const [isTesting, setIsTesting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const addVariable = () => {
    setVariables([...variables, { name: '', value: '' }]);
  };

  const removeVariable = (index: number) => {
    setVariables(variables.filter((_, i) => i !== index));
  };

  const updateVariable = (index: number, field: 'name' | 'value', val: string) => {
    const updated = [...variables];
    updated[index] = { ...updated[index], [field]: val };
    setVariables(updated);
  };

  const handleTest = async () => {
    setError(null);
    setResult(null);
    setIsTesting(true);

    try {
      const sampleValues: Record<string, unknown> = {};
      for (const v of variables) {
        if (v.name.trim()) {
          const num = Number(v.value);
          sampleValues[v.name.trim()] = isNaN(num) ? v.value : num;
        }
      }

      const res = await testFormula(formulaText, sampleValues);
      if (res.success) {
        setResult(`Result: ${JSON.stringify(res.result)}`);
      } else {
        setError(res.error || 'Formula test failed.');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to test formula.');
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Test Formula" size="lg">
      <div className="parameter-test-dialog">
        <TextArea
          label="Natural Language Formula"
          value={formulaText}
          onChange={(e) => setFormulaText(e.target.value)}
          rows={3}
          placeholder="e.g., Gross_Bill minus Discount_Amount"
        />

        <div className="test-variables-section">
          <label className="test-variables-label">Sample Variable Values</label>
          {variables.map((v, i) => (
            <div key={i} className="test-variable-row">
              <Input
                placeholder="Variable name"
                value={v.name}
                onChange={(e) => updateVariable(i, 'name', e.target.value)}
              />
              <Input
                placeholder="Value"
                value={v.value}
                onChange={(e) => updateVariable(i, 'value', e.target.value)}
              />
              <Button variant="ghost" size="sm" onClick={() => removeVariable(i)}>
                ✕
              </Button>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={addVariable}>
            + Add Variable
          </Button>
        </div>

        {error && (
          <Alert type="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        {result && (
          <Alert type="success" onDismiss={() => setResult(null)}>
            {result}
          </Alert>
        )}

        <div className="form-actions" style={{ marginTop: '1rem' }}>
          <Button onClick={handleTest} isLoading={isTesting} disabled={isTesting || !formulaText.trim()}>
            Run Test
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
